import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Button } from '../../ui';
import PremiumName from '../../ui/PremiumName';
import { useUserData } from '../../../hooks/useUserData';
import { useAuth } from '../../../hooks/useAuth';
import { formatPostDate } from '../../../utils/dateFormatter';
import { PortfolioImageModal } from '../../portfolio';
import { deleteOrder } from '../../../services/orderService';
import { isAdminUser } from '../../../constants/admin';

/**
 * OrderCard - Card reutilizable para mostrar una orden de servicio
 * @param {Object} order - Datos de la orden
 * @param {string} viewAs - 'client' o 'seller' - Define qué usuario ver y qué acciones mostrar
 * @param {Function} onViewDetails - Callback al hacer click en ver detalles
 * @param {Function} onAccept - Callback al aceptar (solo seller)
 * @param {Function} onReject - Callback al rechazar (solo seller)
 * @param {Function} onStartProgress - Callback al pasar a en progreso (solo seller, status accepted)
 * @param {Function} onComplete - Callback al completar (solo seller)
 * @param {Function} onCancel - Callback al cancelar
 */
const OrderCard = ({ 
  order, 
  viewAs, 
  onViewDetails,
  onAccept,
  onReject,
  onStartProgress,
  onComplete,
  onCancel,
  onDeleted
}) => {
  const { currentUser } = useAuth();
  const isAdmin = isAdminUser(currentUser);
  // Determinar qué usuario mostrar (el otro, no el actual)
  const otherUserId = viewAs === 'client' ? order.sellerId : order.clientId;
  const otherUserName = viewAs === 'client' ? order.sellerName : order.clientName;
  
  const { userData: otherUserData } = useUserData(otherUserId);

  // Estados y colores
  const statusConfig = {
    pending: {
      label: 'Pendiente',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    accepted: {
      label: 'Aceptado',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    payment_requested: {
      label: 'Pago solicitado',
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    in_progress: {
      label: 'En Progreso',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        </svg>
      )
    },
    completed: {
      label: 'Completado',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    cancelled: {
      label: 'Cancelado',
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  };

  const currentStatusConfig = statusConfig[order.status] || statusConfig.pending;

  const messageCount = Array.isArray(order.messages) ? order.messages.length : 0;
  const hasUnread =
    viewAs === 'client' ? Boolean(order.unreadByClient) : Boolean(order.unreadBySeller);

  const referenceFiles = order.referenceFiles && Array.isArray(order.referenceFiles) ? order.referenceFiles : [];
  const referenceImages = referenceFiles.map((url, i) => ({ url, id: `ref-${order.id}-${i}` }));
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);

  const openImageViewer = (index) => {
    setImageViewerIndex(index);
    setImageViewerOpen(true);
  };

  return (
    <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all">
      <div className="p-5">
        {/* Header con usuario y estado */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Link
            to={`/profile/${otherUserId}`}
            className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
          >
            <Avatar
              src={otherUserData?.photoURL}
              alt={otherUserName}
              fallbackText={otherUserName}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <PremiumName
                name={otherUserData?.displayName || otherUserName || 'Usuario'}
                isPremium={otherUserData?.isPremium}
                premiumColor={otherUserData?.premiumNameColor}
                nameClassName="font-semibold text-gray-900 dark:text-white text-base truncate block"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {viewAs === 'client' ? 'Vendedor' : 'Comprador'}
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Badge de mensajes */}
            {messageCount > 0 && (
              <div
                className={`min-w-[28px] h-7 px-2 inline-flex items-center justify-center rounded-full text-xs font-bold border ${
                  hasUnread
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-200 border-gray-200 dark:border-slate-600'
                }`}
                title={hasUnread ? 'Mensajes pendientes' : 'Mensajes'}
              >
                {messageCount}
              </div>
            )}

            {/* Badge de estado */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-semibold ${currentStatusConfig.color}`}>
              {currentStatusConfig.icon}
              {currentStatusConfig.label}
            </div>
          </div>
        </div>

        {/* Info del servicio */}
        <div className="flex gap-3 mb-3">
          {order.serviceImage && (
            <img
              src={order.serviceImage}
              alt={order.serviceName}
              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base mb-1 truncate">
              {order.serviceName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">
              Paquete: <span className="font-medium">{order.packageName}</span>
            </p>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-bold text-indigo-600 dark:text-indigo-400">
                ${order.amount}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {formatPostDate(order.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Descripción breve */}
        {order.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {order.description}
          </p>
        )}

        {/* Imágenes de referencia del contratante */}
        {referenceImages.length > 0 && (
          <div className="mb-3">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Referencias ({referenceImages.length})</p>
            <div className="flex gap-2 flex-wrap">
              {referenceImages.map((img, index) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => openImageViewer(index)}
                  className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-200 dark:border-slate-600 hover:ring-2 hover:ring-indigo-500 transition-all"
                >
                  <img src={img.url} alt={`Referencia ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Botones de acción según estado y rol */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(order)}
            className="flex-1"
          >
            Ver detalles
          </Button>

          {/* Acciones para VENDEDOR */}
          {viewAs === 'seller' && (
            <>
              {order.status === 'pending' && (
                <>
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={() => onAccept(order)}
                  >
                    Aceptar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onReject(order)}
                    className="text-red-600 hover:text-red-700 border-red-300"
                  >
                    Rechazar
                  </Button>
                </>
              )}

              {(order.status === 'accepted' || order.status === 'payment_requested') && onStartProgress && (
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={() => onStartProgress(order)}
                >
                  Pasar a en progreso
                </Button>
              )}
              
              {order.status === 'in_progress' && (
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={() => onComplete(order)}
                >
                  Marcar completado
                </Button>
              )}
            </>
          )}

          {/* Acciones para CLIENTE */}
          {viewAs === 'client' && (
            <>
              {(order.status === 'pending' || order.status === 'accepted') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancel(order)}
                  className="text-red-600 hover:text-red-700 border-red-300"
                >
                  Cancelar
                </Button>
              )}
            </>
          )}

          {/* Debug (admin-only): eliminar solicitud */}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm('DEBUG: ¿Eliminar esta solicitud/orden? Esta acción no se puede deshacer.')) return;
                const res = await deleteOrder(order.id);
                if (res.success) {
                  if (onDeleted) onDeleted(order.id);
                  alert('Orden eliminada');
                } else {
                  alert(res.error || 'No se pudo eliminar la orden');
                }
              }}
              className="text-red-600 hover:text-red-700 border-red-300"
              title="Debug: eliminar solicitud"
            >
              Debug: eliminar
            </Button>
          )}
        </div>
      </div>

      {/* Visor de imágenes de referencia */}
      {imageViewerOpen && referenceImages.length > 0 && (
        <PortfolioImageModal
          images={referenceImages}
          currentIndex={imageViewerIndex}
          onClose={() => setImageViewerOpen(false)}
          onNavigate={setImageViewerIndex}
        />
      )}
    </div>
  );
};

export default OrderCard;
